import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Play, Pause, RotateCcw, Shield, Target, Radio, Zap, Cpu } from 'lucide-react';

// 35-threat fully autonomous swarm engagement
// Distribution: 18 AIR (5 hardened), 10 SURFACE (3 hardened), 7 UNDERWATER (2 hardened)
// Total: 25 soft-kill + 10 hardened (hard-kill auto-authorized)
const SWARM_TEMPLATES = [
  // ---- AIR (18) ----
  { name: 'FPV Kamikaze',           domain: 'air', color: 0xef4444, speed: 0.10, threatLevel: 'critical', hardened: true  },
  { name: 'Loitering Munition',     domain: 'air', color: 0xef4444, speed: 0.09, threatLevel: 'critical', hardened: true  },
  { name: 'Armed MALE UAV',         domain: 'air', color: 0xdc2626, speed: 0.07, threatLevel: 'critical', hardened: true  },
  { name: 'Heavy Strike Drone',     domain: 'air', color: 0xb91c1c, speed: 0.08, threatLevel: 'critical', hardened: true  },
  { name: 'Stealth Recon UAV',      domain: 'air', color: 0x7f1d1d, speed: 0.09, threatLevel: 'high',     hardened: true  },
  { name: 'Recon Quadcopter A',     domain: 'air', color: 0xf59e0b, speed: 0.07, threatLevel: 'medium',   hardened: false },
  { name: 'Recon Quadcopter B',     domain: 'air', color: 0xf59e0b, speed: 0.07, threatLevel: 'medium',   hardened: false },
  { name: 'Surveillance F-Wing',    domain: 'air', color: 0xf59e0b, speed: 0.08, threatLevel: 'low',      hardened: false },
  { name: 'Swarm Element 1',        domain: 'air', color: 0xff6b6b, speed: 0.11, threatLevel: 'high',     hardened: false },
  { name: 'Swarm Element 2',        domain: 'air', color: 0xff6b6b, speed: 0.10, threatLevel: 'high',     hardened: false },
  { name: 'Swarm Element 3',        domain: 'air', color: 0xff6b6b, speed: 0.10, threatLevel: 'high',     hardened: false },
  { name: 'Swarm Element 4',        domain: 'air', color: 0xff6b6b, speed: 0.11, threatLevel: 'high',     hardened: false },
  { name: 'Swarm Element 5',        domain: 'air', color: 0xff6b6b, speed: 0.10, threatLevel: 'high',     hardened: false },
  { name: 'Decoy Drone Alpha',      domain: 'air', color: 0xfbbf24, speed: 0.08, threatLevel: 'low',      hardened: false },
  { name: 'Decoy Drone Bravo',      domain: 'air', color: 0xfbbf24, speed: 0.08, threatLevel: 'low',      hardened: false },
  { name: 'Decoy Drone Charlie',    domain: 'air', color: 0xfbbf24, speed: 0.08, threatLevel: 'low',      hardened: false },
  { name: 'Comm Relay Drone',       domain: 'air', color: 0xa3a3a3, speed: 0.06, threatLevel: 'medium',   hardened: false },
  { name: 'EW Jammer Drone',        domain: 'air', color: 0xa855f7, speed: 0.07, threatLevel: 'high',     hardened: false },
  // ---- SURFACE (10) ----
  { name: 'Heavy USV Charlie',      domain: 'surface', color: 0x14532d, speed: 0.05,  threatLevel: 'critical', hardened: true  },
  { name: 'Strike USV Heavy',       domain: 'surface', color: 0x166534, speed: 0.055, threatLevel: 'critical', hardened: true  },
  { name: 'Suicide Boat',           domain: 'surface', color: 0x14532d, speed: 0.07,  threatLevel: 'critical', hardened: true  },
  { name: 'Surface USV Alpha',      domain: 'surface', color: 0x22c55e, speed: 0.05,  threatLevel: 'high',     hardened: false },
  { name: 'Surface USV Bravo',      domain: 'surface', color: 0x22c55e, speed: 0.055, threatLevel: 'medium',   hardened: false },
  { name: 'Surface USV Delta',      domain: 'surface', color: 0x22c55e, speed: 0.05,  threatLevel: 'high',     hardened: false },
  { name: 'Recon USV',              domain: 'surface', color: 0x16a34a, speed: 0.045, threatLevel: 'medium',   hardened: false },
  { name: 'Patrol USV',             domain: 'surface', color: 0x22c55e, speed: 0.048, threatLevel: 'medium',   hardened: false },
  { name: 'Strike USV Echo',        domain: 'surface', color: 0x22c55e, speed: 0.052, threatLevel: 'high',     hardened: false },
  { name: 'Recon USV Foxtrot',      domain: 'surface', color: 0x16a34a, speed: 0.045, threatLevel: 'medium',   hardened: false },
  // ---- UNDERWATER (7) ----
  { name: 'Heavy Torpedo UUV',      domain: 'underwater', color: 0x1d4ed8, speed: 0.045, threatLevel: 'critical', hardened: true  },
  { name: 'Stealth Mine Layer',     domain: 'underwater', color: 0x1e3a8a, speed: 0.038, threatLevel: 'critical', hardened: true  },
  { name: 'Subsurface UUV-1',       domain: 'underwater', color: 0x3b82f6, speed: 0.035, threatLevel: 'medium',   hardened: false },
  { name: 'Subsurface UUV-2',       domain: 'underwater', color: 0x2563eb, speed: 0.04,  threatLevel: 'high',     hardened: false },
  { name: 'Subsurface UUV-3',       domain: 'underwater', color: 0x60a5fa, speed: 0.03,  threatLevel: 'medium',   hardened: false },
  { name: 'Subsurface UUV-4',       domain: 'underwater', color: 0x3b82f6, speed: 0.035, threatLevel: 'medium',   hardened: false },
  { name: 'Subsurface UUV-5',       domain: 'underwater', color: 0x2563eb, speed: 0.04,  threatLevel: 'high',     hardened: false },
];

const SOFT_KILL_METHODS = {
  air: [
    { id: 'rf_jamming', label: 'RF Jamming', color: '#eab308', behavior: 'tumble_down' },
    { id: 'cyber_takeover', label: 'Cyber Takeover', color: '#a855f7', behavior: 'spiral_down' },
    { id: 'gps_spoofing', label: 'GPS Spoofing', color: '#3b82f6', behavior: 'wander_away' },
  ],
  surface: [
    { id: 'rf_jamming', label: 'RF Jamming', color: '#eab308', behavior: 'tumble_down' },
    { id: 'gps_spoofing', label: 'GPS Spoofing', color: '#3b82f6', behavior: 'wander_away' },
  ],
  underwater: [
    { id: 'acoustic_blaster', label: 'Acoustic Blaster', color: '#06b6d4', behavior: 'acoustic_pushback' },
  ],
};

const HARD_KILL_OPTIONS = {
  directed_energy:    { label: 'DEW',                color: '#22c55e', effectColor: 0x00ff00 },
  anti_drone_gun:     { label: 'Anti Drone Gun',     color: '#eab308', effectColor: 0xffcc00 },
  kinetic_interceptor:{ label: 'Kinetic Interceptor',color: '#f97316', effectColor: 0xff6600 },
  net_capture:        { label: 'Net Capture',        color: '#06b6d4', effectColor: 0x00ffff },
  anti_drone_missile: { label: 'Anti-Drone Missile', color: '#ef4444', effectColor: 0xff0000 },
};

// AI auto-selects the best hard-kill per domain (the "rank #1" pick)
const AUTO_HARD_KILL = {
  air: 'directed_energy',
  surface: 'anti_drone_gun',
  underwater: 'anti_drone_missile',
};

export default function SimulationScenario5() {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const animationRef = useRef(null);

  const playingRef = useRef(false);
  const threatsRef = useRef([]);
  const spawnedRef = useRef(false);
  const statsRef = useRef({ detected: 0, soft_killed: 0, hard_killed: 0, neutralized: 0 });
  const logsRef = useRef([]);
  const startTimeRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [logs, setLogs] = useState([]);
  const [activeThreats, setActiveThreats] = useState([]);
  const [stats, setStats] = useState({ detected: 0, soft_killed: 0, hard_killed: 0, neutralized: 0 });
  const [elapsed, setElapsed] = useState(0);

  const pushLog = useCallback((message, type = 'info') => {
    const entry = { ts: new Date().toLocaleTimeString(), message, type };
    logsRef.current = [...logsRef.current.slice(-100), entry];
    setLogs([...logsRef.current]);
  }, []);

  const bumpStat = useCallback((field) => {
    statsRef.current = { ...statsRef.current, [field]: statsRef.current[field] + 1 };
    setStats({ ...statsRef.current });
  }, []);

  const syncActiveThreats = useCallback(() => {
    const list = threatsRef.current
      .filter(t => t.visible && t.userData.status !== 'neutralized' && t.userData.status !== 'destroyed')
      .map(t => ({
        id: t.userData.id, name: t.userData.name, domain: t.userData.domain,
        status: t.userData.status, threatLevel: t.userData.threatLevel,
        hardened: t.userData.hardened,
        distance: t.position.distanceTo(new THREE.Vector3(0, 0, 0)).toFixed(1),
        soft_kill_label: t.userData.soft_kill_label,
        hard_kill_label: t.userData.hard_kill_label,
      }));
    setActiveThreats(list);
  }, []);

  const cbRef = useRef({ pushLog, bumpStat, syncActiveThreats });
  useEffect(() => {
    cbRef.current = { pushLog, bumpStat, syncActiveThreats };
  }, [pushLog, bumpStat, syncActiveThreats]);

  // Hard-kill execution helper (auto-fired by the simulation)
  const fireHardKill = (target, optionId) => {
    const option = HARD_KILL_OPTIONS[optionId];
    if (!target || !option || !sceneRef.current) return;
    target.userData.hard_kill_label = option.label;
    cbRef.current.pushLog(`[AUTO HARD KILL] ${target.userData.id} → ${option.label}`, 'danger');

    // Weapon-specific visual
    if (optionId === 'directed_energy') {
      const beam = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 1.5, 0), target.position.clone()]),
        new THREE.LineBasicMaterial({ color: option.effectColor })
      );
      beam.userData.isEffect = true; beam.userData.life = 1.0;
      sceneRef.current.add(beam);
    } else if (optionId === 'anti_drone_gun') {
      for (let i = 0; i < 8; i++) {
        const tracer = new THREE.Mesh(
          new THREE.SphereGeometry(0.18, 8, 8),
          new THREE.MeshBasicMaterial({ color: option.effectColor, transparent: true, opacity: 1 })
        );
        const t = (i + 1) / 8;
        tracer.position.copy(new THREE.Vector3(0, 1, 0).clone().lerp(target.position, t));
        tracer.position.y += (Math.random() - 0.5) * 0.3;
        tracer.userData.isEffect = true; tracer.userData.life = 0.8;
        sceneRef.current.add(tracer);
      }
    } else if (optionId === 'anti_drone_missile' || optionId === 'kinetic_interceptor') {
      const trail = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 1.5, 0), target.position.clone()]),
        new THREE.LineBasicMaterial({ color: option.effectColor })
      );
      trail.userData.isEffect = true; trail.userData.life = 0.9;
      sceneRef.current.add(trail);
    }

    const explosion = new THREE.Mesh(
      new THREE.SphereGeometry(1.4, 16, 16),
      new THREE.MeshBasicMaterial({ color: option.effectColor, transparent: true, opacity: 0.9 })
    );
    explosion.position.copy(target.position);
    explosion.userData.isEffect = true; explosion.userData.expands = true; explosion.userData.life = 1.0;
    sceneRef.current.add(explosion);

    setTimeout(() => {
      target.userData.status = 'hard_kill_active';
      cbRef.current.bumpStat('hard_killed');
      cbRef.current.pushLog(`[DESTROYED] ${target.userData.id} eliminated by ${option.label}`, 'success');
      cbRef.current.syncActiveThreats();
    }, 250);
  };

  const fireHardKillRef = useRef(fireHardKill);
  useEffect(() => { fireHardKillRef.current = fireHardKill; });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    playingRef.current = false;
    threatsRef.current = [];
    spawnedRef.current = false;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x020617);
    scene.fog = new THREE.FogExp2(0x020617, 0.0035);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(55, container.clientWidth / container.clientHeight, 0.1, 1000);
    camera.position.set(60, 45, 60);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 20;
    controls.maxDistance = 180;

    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(10, 20, 10);
    scene.add(dirLight);

    // Ocean
    const ocean = new THREE.Mesh(
      new THREE.PlaneGeometry(420, 420),
      new THREE.MeshStandardMaterial({ color: 0x0c4a6e, transparent: true, opacity: 0.3, side: THREE.DoubleSide })
    );
    ocean.rotation.x = -Math.PI / 2; ocean.position.y = -1;
    scene.add(ocean);

    const grid = new THREE.GridHelper(300, 60, 0x1e3a5f, 0x0f2847);
    grid.position.y = -0.9;
    scene.add(grid);

    // Ship
    const shipGroup = new THREE.Group();
    const s = 0.65;
    const hull = new THREE.Mesh(new THREE.BoxGeometry(2*s, 1*s, 6*s), new THREE.MeshStandardMaterial({ color: 0xfbbf24, metalness: 0.5, roughness: 0.3 }));
    hull.position.set(0, -0.3*s, 0); shipGroup.add(hull);
    const deck = new THREE.Mesh(new THREE.BoxGeometry(1.8*s, 0.2*s, 5.5*s), new THREE.MeshStandardMaterial({ color: 0xf59e0b }));
    deck.position.set(0, 0.2*s, 0); shipGroup.add(deck);
    const bridge = new THREE.Mesh(new THREE.BoxGeometry(1.2*s, 1*s, 1.5*s), new THREE.MeshStandardMaterial({ color: 0xeab308 }));
    bridge.position.set(0, 0.8*s, -0.8*s); shipGroup.add(bridge);
    const dome = new THREE.Mesh(new THREE.SphereGeometry(0.22*s, 16, 16), new THREE.MeshStandardMaterial({ color: 0x0ea5e9, emissive: 0x0ea5e9, emissiveIntensity: 0.6 }));
    dome.position.set(0, 2.1*s, -0.8*s); shipGroup.add(dome);
    scene.add(shipGroup);

    // Defense zones
    const addZone = (radius, color, opacity) => {
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(radius, 32, 32),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity, wireframe: true, side: THREE.DoubleSide })
      );
      sphere.userData.isZone = true;
      scene.add(sphere);
    };
    addZone(15, 0x0ea5e9, 0.05);
    addZone(6, 0xeab308, 0.08);
    addZone(3, 0xef4444, 0.10);
    [15, 6, 3].forEach(r => {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(r - 0.05, r + 0.05, 64),
        new THREE.MeshBasicMaterial({ color: 0x1e3a5f, side: THREE.DoubleSide })
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = -0.8;
      scene.add(ring);
    });

    function createThreatMesh(domain, color) {
      let geo;
      if (domain === 'air') geo = new THREE.TetrahedronGeometry(0.85, 0);
      else if (domain === 'surface') geo = new THREE.BoxGeometry(1.1, 0.55, 0.85);
      else geo = new THREE.SphereGeometry(0.6, 24, 24);
      const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
        color, emissive: color, emissiveIntensity: 0.65, roughness: 0.4
      }));
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.85, 0.95, 32),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.6, side: THREE.DoubleSide })
      );
      ring.rotation.x = Math.PI / 2;
      mesh.add(ring);
      return mesh;
    }

    function spawnSwarm() {
      if (spawnedRef.current) return;
      spawnedRef.current = true;
      startTimeRef.current = Date.now();
      const yMap = { air: () => 6 + Math.random() * 8, surface: () => 0.4, underwater: () => -3.5 - Math.random() * 3 };

      SWARM_TEMPLATES.forEach((tpl, idx) => {
        const angle = (idx / SWARM_TEMPLATES.length) * Math.PI * 2 + (Math.random() * 0.12);
        const dist = 26 + Math.random() * 8;
        const startPos = new THREE.Vector3(Math.cos(angle) * dist, yMap[tpl.domain](), Math.sin(angle) * dist);
        const id = `T-${String(idx + 1).padStart(2, '0')}`;
        const mesh = createThreatMesh(tpl.domain, tpl.color);
        mesh.position.copy(startPos);
        if (tpl.hardened) {
          const halo = new THREE.Mesh(
            new THREE.RingGeometry(1.2, 1.45, 32),
            new THREE.MeshBasicMaterial({ color: 0xff0000, transparent: true, opacity: 0.5, side: THREE.DoubleSide })
          );
          halo.rotation.x = Math.PI / 2;
          halo.userData.isHalo = true;
          mesh.add(halo);
        }
        mesh.userData = {
          id, name: tpl.name, domain: tpl.domain, color: tpl.color,
          speed: tpl.speed * (0.85 + Math.random() * 0.3),
          threatLevel: tpl.threatLevel, hardened: tpl.hardened,
          status: 'approaching', detected: false, tracking: false,
          softKillApplied: false, soft_kill_label: null, hard_kill_label: null,
        };
        scene.add(mesh);
        threatsRef.current.push(mesh);
      });

      const hardenedCount = SWARM_TEMPLATES.filter(t => t.hardened).length;
      cbRef.current.pushLog(`[ALERT] MASS SATURATION SWARM — ${SWARM_TEMPLATES.length} threats; AUTO ENGAGE: ${SWARM_TEMPLATES.length - hardenedCount} soft-kill / ${hardenedCount} hard-kill`, 'danger');
      cbRef.current.syncActiveThreats();
    }

    function updateThreats() {
      const ship = new THREE.Vector3(0, 0, 0);
      let stateChanged = false;

      threatsRef.current.forEach(threat => {
        if (!threat.visible) return;
        const ud = threat.userData;
        if (ud.status === 'neutralized' || ud.status === 'destroyed') return;

        // Soft-killed animation (same as Scenario 3/4)
        if (ud.status === 'soft_killed') {
          const beh = ud.kill_behavior;
          const colorHex = ud.kill_color || 0xffffff;
          ud.killTick = (ud.killTick || 0) + 1;

          if (beh === 'spiral_down') {
            const t = ud.killTick;
            const radius = 0.04 * t;
            const center = ud.killOrigin;
            threat.position.x = center.x + Math.cos(t * 0.18) * radius;
            threat.position.z = center.z + Math.sin(t * 0.18) * radius;
            threat.position.y -= 0.06;
            threat.rotation.y += 0.18;
            threat.rotation.x += 0.04;
            if (t % 4 === 0) {
              const p = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 8),
                new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.85 }));
              p.position.copy(threat.position);
              p.userData.isEffect = true; p.userData.life = 0.8;
              scene.add(p);
            }
          } else if (beh === 'tumble_down') {
            threat.position.y -= 0.07;
            threat.rotation.x += 0.13 + Math.sin(ud.killTick * 0.3) * 0.05;
            threat.rotation.z += 0.10 + Math.cos(ud.killTick * 0.4) * 0.05;
            threat.position.x += Math.sin(ud.killTick * 0.4) * 0.04;
            threat.position.z += Math.cos(ud.killTick * 0.35) * 0.04;
          } else if (beh === 'wander_away') {
            if (!ud.wanderDir) {
              const baseAway = threat.position.clone().normalize();
              const perp = new THREE.Vector3(-baseAway.z, 0, baseAway.x).multiplyScalar(0.5 * (Math.random() - 0.5));
              ud.wanderDir = baseAway.add(perp).normalize();
            }
            threat.position.add(ud.wanderDir.clone().multiplyScalar(0.09));
            if (ud.domain === 'air') threat.position.y += Math.sin(ud.killTick * 0.1) * 0.05;
            threat.rotation.y += 0.04;
          } else if (beh === 'acoustic_pushback') {
            const radial = new THREE.Vector3(threat.position.x, 0, threat.position.z).normalize();
            threat.position.add(radial.multiplyScalar(0.10));
            threat.position.y -= 0.025;
            threat.rotation.x += 0.08;
            threat.rotation.z += 0.08;
          }

          if (threat.material) {
            threat.material.transparent = true;
            threat.material.opacity = Math.max(0.0, (threat.material.opacity ?? 1) - 0.008);
          }
          const dist2 = threat.position.distanceTo(ship);
          const finished =
            (threat.material && threat.material.opacity <= 0.05) ||
            threat.position.y < -8 ||
            (beh === 'wander_away' && dist2 > 50) ||
            (beh === 'acoustic_pushback' && dist2 > 40);
          if (finished) {
            threat.visible = false;
            ud.status = 'neutralized';
            cbRef.current.bumpStat('neutralized');
            stateChanged = true;
          }
          return;
        }

        if (ud.status === 'hard_kill_active') {
          threat.scale.multiplyScalar(0.90);
          threat.rotation.x += 0.08;
          threat.rotation.z += 0.08;
          if (threat.scale.x < 0.08) {
            threat.visible = false;
            ud.status = 'destroyed';
            cbRef.current.bumpStat('neutralized');
            stateChanged = true;
          }
          return;
        }

        // Approach
        const dist = threat.position.distanceTo(ship);
        const dir = new THREE.Vector3().subVectors(ship, threat.position).normalize();
        const targetY = ud.domain === 'air' ? 4 : ud.domain === 'underwater' ? -3 : 0.4;
        dir.y = (targetY - threat.position.y) * 0.02;
        threat.position.add(dir.multiplyScalar(ud.speed));
        threat.rotation.x += 0.012;
        threat.rotation.y += 0.018;
        const sc = 1 + Math.sin(Date.now() * 0.004) * 0.10;
        threat.scale.setScalar(sc);
        threat.children.forEach(ch => {
          if (ch.userData.isHalo) ch.material.opacity = 0.4 + Math.abs(Math.sin(Date.now() * 0.006)) * 0.4;
        });

        // 5km detect
        if (dist < 16 && !ud.detected) {
          ud.detected = true;
          ud.status = 'detected';
          const tag = ud.hardened ? '⚠ HARDENED' : ud.threatLevel.toUpperCase();
          cbRef.current.pushLog(`[5KM] ${ud.id} ${ud.name} DETECTED — ${tag} (${ud.domain})`, ud.hardened ? 'danger' : 'warning');
          cbRef.current.bumpStat('detected');
          stateChanged = true;
        }

        // 4km tracking
        if (dist < 13 && ud.detected && !ud.tracking) {
          ud.tracking = true;
          ud.status = 'tracking';
          stateChanged = true;
        }

        // 2km — soft-kill attempt (always tries soft-kill first)
        if (dist < 7 && !ud.softKillApplied) {
          ud.softKillApplied = true;
          const methods = SOFT_KILL_METHODS[ud.domain];
          const sk = methods[Math.floor(Math.random() * methods.length)];
          ud.soft_kill_label = sk.label;
          ud.kill_behavior = sk.behavior;
          ud.kill_color = parseInt(sk.color.replace('#', '0x'));
          ud.killOrigin = threat.position.clone();

          cbRef.current.pushLog(`[2KM] ${ud.id} AUTO SOFT KILL: ${sk.label}`, 'info');
          const colorHex = ud.kill_color;

          if (sk.behavior === 'acoustic_pushback') {
            for (let i = 0; i < 4; i++) {
              const ring = new THREE.Mesh(
                new THREE.RingGeometry(0.4 + i * 0.5, 0.55 + i * 0.5, 48),
                new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.9, side: THREE.DoubleSide })
              );
              ring.position.set(0, threat.position.y, 0);
              ring.rotation.x = -Math.PI / 2;
              ring.userData.isEffect = true; ring.userData.life = 1.0; ring.userData.expands = true;
              scene.add(ring);
            }
          } else if (sk.behavior === 'spiral_down') {
            const cage = new THREE.Mesh(
              new THREE.BoxGeometry(2, 2, 2),
              new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.5, wireframe: true })
            );
            cage.position.copy(threat.position);
            cage.userData.isEffect = true; cage.userData.life = 1.0;
            scene.add(cage);
          } else {
            for (let i = 0; i < 4; i++) {
              const wave = new THREE.Mesh(
                new THREE.RingGeometry(0.4 + i * 0.6, 0.55 + i * 0.6, 32),
                new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.7, side: THREE.DoubleSide })
              );
              wave.position.copy(threat.position);
              wave.lookAt(ship);
              wave.userData.isEffect = true; wave.userData.life = 1.0;
              scene.add(wave);
            }
          }

          setTimeout(() => {
            if (ud.hardened) {
              // AUTO escalate to hard-kill (no operator prompt)
              cbRef.current.pushLog(`[ESCALATE] ${ud.id} soft kill failed — auto-engaging hard kill`, 'danger');
              const optionId = AUTO_HARD_KILL[ud.domain] || 'directed_energy';
              fireHardKillRef.current(threat, optionId);
            } else {
              ud.status = 'soft_killed';
              cbRef.current.pushLog(`[NEUTRALIZED] ${ud.id} soft kill SUCCESS — ${sk.label}`, 'success');
              cbRef.current.bumpStat('soft_killed');
            }
            cbRef.current.syncActiveThreats();
          }, 600 + Math.random() * 500);
          stateChanged = true;
        }
      });

      // Effects cleanup
      const toRemove = [];
      scene.children.forEach(c => {
        if (c.userData.isEffect) {
          c.userData.life -= 0.02;
          if (c.userData.expands) c.scale.setScalar(1 + (1 - c.userData.life) * 4);
          if (c.material) c.material.opacity = Math.max(c.userData.life, 0);
          if (c.userData.life <= 0) toRemove.push(c);
        }
      });
      toRemove.forEach(o => scene.remove(o));

      if (stateChanged) cbRef.current.syncActiveThreats();
    }

    let frame = 0;
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      frame++;
      if (playingRef.current) {
        if (!spawnedRef.current) spawnSwarm();
        updateThreats();
        if (frame % 15 === 0) cbRef.current.syncActiveThreats();
        if (frame % 30 === 0 && startTimeRef.current) {
          setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }
      }
      scene.children.forEach(c => { if (c.userData.isZone) c.rotation.y += 0.0008; });
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!container) return;
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
      playingRef.current = false;
      if (container && renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = () => {
    playingRef.current = !playingRef.current;
    setIsPlaying(playingRef.current);
    if (playingRef.current && logsRef.current.length === 0) {
      pushLog('Scenario 5 started — 35-threat fully autonomous engagement (auto soft-kill + auto hard-kill)', 'info');
    }
  };

  const resetSim = () => {
    playingRef.current = false;
    setIsPlaying(false);
    spawnedRef.current = false;
    setElapsed(0);
    startTimeRef.current = null;
    logsRef.current = [];
    setLogs([]);
    setActiveThreats([]);
    statsRef.current = { detected: 0, soft_killed: 0, hard_killed: 0, neutralized: 0 };
    setStats({ ...statsRef.current });
    if (sceneRef.current) {
      const toRemove = [];
      sceneRef.current.children.forEach(c => {
        if (c.userData.isEffect) toRemove.push(c);
      });
      threatsRef.current.forEach(t => toRemove.push(t));
      toRemove.forEach(o => sceneRef.current.remove(o));
      threatsRef.current = [];
    }
    pushLog('Scenario 5 reset — Ready', 'info');
  };

  const getStatusColor = (s) => ({
    approaching: 'text-zinc-400', detected: 'text-yellow-500', tracking: 'text-orange-400',
    soft_killed: 'text-green-400', hard_kill_active: 'text-red-500',
    destroyed: 'text-emerald-500', neutralized: 'text-emerald-500'
  }[s] || 'text-zinc-400');
  const getLogColor = (t) => ({ warning: 'text-yellow-400', danger: 'text-red-400', success: 'text-emerald-400' }[t] || 'text-zinc-400');
  const domainBadge = (d) => ({
    air:        { c: 'text-sky-400 bg-sky-500/10 border-sky-500/40',         label: 'AIR ▲'  },
    surface:    { c: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/40', label: 'SURF ■' },
    underwater: { c: 'text-blue-400 bg-blue-500/10 border-blue-500/40',     label: 'SUB ●'  },
  }[d] || { c: '', label: d });

  const totalThreats = SWARM_TEMPLATES.length;
  const hardenedCount = SWARM_TEMPLATES.filter(t => t.hardened).length;
  const allDone = stats.neutralized >= totalThreats;
  const fmtTime = (s) => `${Math.floor(s / 60)}m ${(s % 60).toString().padStart(2, '0')}s`;

  return (
    <div className="h-full flex flex-col bg-zinc-950" data-testid="simulation-scenario5">
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 relative">
          <div ref={containerRef} className="w-full h-full" />

          <div className="absolute top-4 left-4 flex gap-2" data-testid="sim5-stats">
            {[
              { label: 'Inbound',     val: totalThreats,         color: 'text-zinc-300 border-zinc-700 bg-zinc-800/70' },
              { label: 'Detected',    val: stats.detected,       color: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10' },
              { label: 'Soft Kill',   val: stats.soft_killed,    color: 'text-green-400 border-green-500/30 bg-green-500/10' },
              { label: 'Hard Kill',   val: stats.hard_killed,    color: 'text-red-400 border-red-500/30 bg-red-500/10' },
              { label: 'Neutralized', val: stats.neutralized,    color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
            ].map(st => (
              <div key={st.label} className={`px-3 py-2 rounded border backdrop-blur-md text-center ${st.color}`}>
                <div className="text-lg font-bold font-mono">{st.val}</div>
                <div className="text-[10px] uppercase">{st.label}</div>
              </div>
            ))}
            <div className="px-3 py-2 rounded border bg-fuchsia-500/10 border-fuchsia-500/40 text-fuchsia-300 text-center">
              <div className="text-lg font-bold font-mono">{fmtTime(elapsed)}</div>
              <div className="text-[10px] uppercase">Engagement</div>
            </div>
          </div>

          <div className="absolute top-4 right-4 max-w-xs bg-black/70 backdrop-blur-md border border-fuchsia-500/40 p-3 rounded-sm">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Cpu size={14} className="text-fuchsia-400" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-fuchsia-300">Scenario 5 — Full Autonomy</h3>
            </div>
            <p className="text-[11px] text-zinc-400 leading-snug">
              <span className="text-amber-300">35-threat</span> swarm — <span className="text-emerald-300">{totalThreats - hardenedCount} soft-kill</span> + <span className="text-red-300">{hardenedCount} hard-kill</span>, both fully automated. No operator authorization required.
            </p>
            <div className="mt-2 space-y-0.5 text-[10px] font-mono text-zinc-400">
              <div><span className="text-sky-400">▲ AIR (18)</span> — 5 hardened (auto: DEW)</div>
              <div><span className="text-emerald-400">■ SURF (10)</span> — 3 hardened (auto: Gun)</div>
              <div><span className="text-blue-400">● SUB (7)</span> — 2 hardened (auto: Missile)</div>
            </div>
          </div>

          <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur-md border border-zinc-800 p-3 rounded-sm">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 text-fuchsia-400 flex items-center gap-1">
              <Shield size={14} /> Defense Layers
            </h3>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-sky-500" /><span className="text-zinc-400">5km — Detect &amp; Track</span></div>
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-yellow-500" /><span className="text-zinc-400">2km — Auto Soft Kill</span></div>
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-red-500" /><span className="text-zinc-400">1km — Auto Hard Kill</span></div>
            </div>
          </div>

          {allDone && (
            <div className="absolute inset-x-0 top-1/3 flex items-center justify-center pointer-events-none" data-testid="sim5-all-clear">
              <div className="bg-fuchsia-500/15 backdrop-blur-md border-2 border-fuchsia-300 rounded-lg px-8 py-4 text-center">
                <div className="text-2xl font-bold uppercase tracking-widest text-fuchsia-200">All Clear</div>
                <div className="text-xs text-fuchsia-100 font-mono mt-1">
                  Mass swarm fully neutralized in {fmtTime(elapsed)} — {stats.soft_killed} soft / {stats.hard_killed} hard (autonomous)
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div className="w-80 bg-zinc-900/50 border-l border-zinc-800 flex flex-col">
          <div className="p-3 border-b border-zinc-800 flex items-center gap-2">
            <button onClick={togglePlay}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded font-mono text-xs uppercase tracking-wider transition-all ${
                isPlaying ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50' : 'bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/50'
              }`} data-testid="s5-play-btn">
              {isPlaying ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Auto Engage</>}
            </button>
            <button onClick={resetSim}
              className="flex items-center gap-1 px-3 py-2 rounded font-mono text-xs uppercase bg-zinc-700/50 text-zinc-300 border border-zinc-600 hover:bg-zinc-700 transition-all"
              data-testid="s5-reset-btn">
              <RotateCcw size={14} /> Reset
            </button>
          </div>

          <div className="p-3 border-b border-zinc-800">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Target size={14} className="text-red-500" /> Active Threats
              <span className="ml-auto text-zinc-500 font-mono">{activeThreats.length}/{totalThreats}</span>
            </h3>
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {activeThreats.length === 0 ? (
                <p className="text-xs text-zinc-500">{isPlaying ? 'All threats neutralized' : 'Press AUTO ENGAGE to spawn 35-threat swarm'}</p>
              ) : (
                activeThreats.map(t => {
                  const b = domainBadge(t.domain);
                  return (
                    <div key={t.id} className="flex items-center justify-between text-xs bg-zinc-800/50 rounded px-2 py-1.5" data-testid={`s5-threat-${t.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-white font-mono">{t.id}</span>
                          <span className={`text-[9px] px-1 rounded border ${b.c}`}>{b.label}</span>
                          {t.hardened && (
                            <span className="text-[9px] px-1 rounded border bg-red-500/15 text-red-300 border-red-500/40">⚠</span>
                          )}
                        </div>
                        <div className="text-zinc-500 truncate">{t.name}</div>
                        {t.soft_kill_label && (
                          <div className="text-[10px] text-amber-400 flex items-center gap-1"><Zap size={9} /> {t.soft_kill_label}</div>
                        )}
                        {t.hard_kill_label && (
                          <div className="text-[10px] text-red-400 flex items-center gap-1">⚔ {t.hard_kill_label}</div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-zinc-500 font-mono text-[10px]">{(parseFloat(t.distance) * 0.333).toFixed(1)}km</div>
                        <div className={`font-mono uppercase text-[10px] ${getStatusColor(t.status)}`}>{t.status.replace(/_/g, ' ')}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex-1 p-3 flex flex-col min-h-0">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Radio size={14} /> Event Log
            </h3>
            <div className="flex-1 overflow-y-auto space-y-0.5 font-mono text-[11px] bg-black/30 rounded p-2" data-testid="s5-event-log">
              {logs.length === 0 ? (
                <p className="text-zinc-500">Press AUTO ENGAGE to begin...</p>
              ) : (
                logs.map((l, i) => (
                  <div key={i} className={getLogColor(l.type)}>
                    <span className="text-zinc-600">[{l.ts}]</span> {l.message}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
